import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Target, Eye, Gauge, ShieldCheck, Layers, HardHat, Calculator, CalendarRange, PackageSearch, FileStack, Search, Truck } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskLines, Marquee, EASE } from "@/components/Reveal";
import Faq from "@/components/Faq";
import CtaBand from "@/components/CtaBand";
import { HOME } from "@/content/home";
import { SERVICES } from "@/content/services";
import { SECTORS } from "@/content/pages";
import { MOON } from "@/content/moon";
import { UI } from "@/content/ui";
import { useLang } from "@/lib/lang";

const ICONS = { Layers, HardHat, Calculator, CalendarRange, PackageSearch, FileStack, Target, Eye, Gauge, ShieldCheck, Search, Truck };

const HERO_IMG = "https://images.unsplash.com/photo-1644221150141-24f57d9a468b?auto=format&fit=crop&w=1400&q=80";
const ABOUT_IMG = "https://images.unsplash.com/photo-1535940762834-e5334814e85f?auto=format&fit=crop&w=1400&q=80";

export default function Home() {
  const { t, lang } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const bentoSpans = ["md:col-span-4", "md:col-span-2", "md:col-span-2", "md:col-span-4", "md:col-span-3", "md:col-span-3"];

  return (
    <main data-testid="home-page">
      <Seo
        title={lang === "fr"
          ? "Export Monde Prestige Inc. (EMP) | Approvisionnement, gestion de projets et solutions numériques"
          : "Export Monde Prestige Inc. (EMP) | Procurement, project management & digital solutions"}
        description={t(HOME.hero.sub)}
        path="/"
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-gradient-to-b from-ice via-white to-white pt-[76px]" data-testid="home-hero">
        <div className="pointer-events-none absolute inset-0 opacity-[0.35]" style={{ backgroundImage: "linear-gradient(rgba(2,132,199,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(2,132,199,.07) 1px, transparent 1px)", backgroundSize: "72px 72px" }} />
        <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-sky-100/70 blur-[120px]" />

        <div className="relative mx-auto grid w-full max-w-[1320px] items-center gap-14 px-5 py-16 md:px-8 lg:grid-cols-12 lg:gap-8 lg:py-20">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="mb-7 inline-flex items-center gap-3 rounded-full border border-sky-200 bg-white px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest2 text-sky-700 shadow-card"
            >
              <span className="h-1.5 w-1.5 animate-pulseGlow rounded-full bg-turquoise" />
              {t(HOME.hero.eyebrow)}
            </motion.p>
            <h1 className="font-display text-[2.4rem] font-bold leading-[1.06] tracking-tight text-navy-950 sm:text-5xl lg:text-[3.8rem]" data-testid="hero-title">
              <MaskLines lines={HOME.hero.lines[lang]} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
              className="mt-7 max-w-xl text-base leading-relaxed text-graphite md:text-lg"
              data-testid="hero-subtitle"
            >
              {t(HOME.hero.sub)}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/moon/recherche" data-testid="hero-cta-equipment" className="group inline-flex items-center gap-2 rounded-full bg-electric px-7 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-electric-dark">
                <Search size={16} /> {t(HOME.hero.ctaSearch)}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/contact" data-testid="hero-cta-quote" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 text-sm font-bold text-navy-800 transition-colors duration-300 hover:border-electric hover:text-electric">
                {t(HOME.hero.ctaQuote)}
              </Link>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.5, ease: EASE }} className="relative">
              <div className="absolute -inset-3 rounded-2xl border-2 border-electric/20" style={{ transform: "translate(14px, 14px)" }} />
              <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-2xl shadow-lift" data-testid="hero-image-frame">
                <img src={HERO_IMG} alt={t(HOME.hero.imgAlt)} className="aspect-[4/5] w-full object-cover" loading="eager" fetchpriority="high" />
                <div className="absolute inset-0 bg-gradient-to-t from-sky-900/30 via-transparent to-transparent" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
                className="absolute -bottom-8 -left-4 max-w-[260px] rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-lift backdrop-blur-xl md:-left-12"
                data-testid="hero-floating-card"
              >
                <p className="font-mono text-[10px] font-semibold uppercase tracking-widest2 text-sky-700">{lang === "fr" ? "Nos pôles" : "Our arms"}</p>
                <ul className="mt-3 space-y-2 text-[13px] font-medium text-navy-800">
                  {["MOON Supply", "EMP Projects", "EMP Digital"].map((x) => (
                    <li key={x} className="flex items-center gap-2"><span className="h-1 w-3 rounded-full bg-electric" />{x}</li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={HOME.marquee[lang]} />

      {/* TROIS PÔLES */}
      <section className="bg-white py-20 md:py-28" data-testid="home-poles-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(HOME.poles.eyebrow)}</p>
            <div className="mt-4 flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <h2 className="max-w-xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-5xl">{t(HOME.poles.title)}</h2>
              <p className="max-w-md text-[15px] leading-relaxed text-graphite">{t(HOME.poles.intro)}</p>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {HOME.poles.items.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <Reveal key={p.slug} delay={i * 0.07} className="h-full">
                  <Link
                    to={p.to}
                    data-testid={`home-pole-card-${p.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-slate-200/90 bg-paper p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-sky-300 hover:bg-white hover:shadow-lift"
                  >
                    <span className="inline-flex w-fit rounded-full bg-badge px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest2 text-sky-700">{p.tag}</span>
                    <span className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-navy-950 text-white transition-colors duration-300 group-hover:bg-electric">
                      <Icon size={22} strokeWidth={1.8} />
                    </span>
                    <h3 className="mt-5 font-display text-2xl font-bold text-navy-950">{t(p.title)}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-graphite">{t(p.desc)}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-6 text-[13px] font-bold text-electric">
                      {p.slug === "moon" ? t(MOON.ctaSearch) : lang === "fr" ? "Découvrir le pôle" : "Explore this arm"}
                      <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ÉQUIPEMENTS RARES */}
      <section className="bg-white pb-20 md:pb-28">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-electric px-7 py-12 md:px-14 md:py-16" data-testid="home-rare-band">
              <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
              <div className="relative grid items-center gap-8 lg:grid-cols-12">
                <div className="lg:col-span-8">
                  <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-turquoise-bright">{t(HOME.rare.eyebrow)}</p>
                  <h2 className="mt-4 font-display text-3xl font-bold leading-[1.1] text-white md:text-4xl">{t(HOME.rare.title)}</h2>
                  <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85 md:text-base">{t(HOME.rare.sub)}</p>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <Link to="/moon/recherche" data-testid="home-rare-cta" className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-bold text-electric-dark transition-colors hover:bg-ice">
                    {t(HOME.rare.cta)} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FONCTIONNEMENT */}
      <section className="bg-paper py-20 md:py-28" data-testid="home-steps-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(HOME.steps.eyebrow)}</p>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-5xl">{t(HOME.steps.title)}</h2>
          </Reveal>
          <div className="mt-14 grid gap-10 md:grid-cols-4 md:gap-6">
            {HOME.steps.steps.map((step, i) => (
              <Reveal key={i} delay={i * 0.08} className="h-full">
                <div className="relative h-full border-t-2 border-electric/40 pt-6" data-testid={`home-step-${i}`}>
                  <span className="font-mono text-sm font-semibold text-sky-700">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-xl font-semibold text-navy-950">{t(step.title)}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-graphite">{t(step.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES DÉTAILLÉS — BENTO */}
      <section className="bg-white py-20 md:py-28" data-testid="home-services-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(HOME.domains.eyebrow)}</p>
            <div className="mt-4 flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <h2 className="max-w-xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-5xl">{t(HOME.domains.title)}</h2>
              <p className="max-w-md text-[15px] leading-relaxed text-graphite">{t(HOME.domains.intro)}</p>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.slug} delay={i * 0.06} className={`${bentoSpans[i]} h-full`}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`home-service-card-${s.slug}`}
                    className="group flex h-full flex-col justify-between rounded-2xl border border-slate-200/90 bg-paper p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-sky-300 hover:bg-white hover:shadow-lift"
                  >
                    <div>
                      <div className="flex items-start justify-between">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-badge text-sky-700 transition-colors duration-300 group-hover:bg-electric group-hover:text-white">
                          <Icon size={20} strokeWidth={1.8} />
                        </span>
                        <span className="font-display text-3xl font-light text-slate-200 transition-colors duration-300 group-hover:text-sky-300">{s.num}</span>
                      </div>
                      <h3 className="mt-6 font-display text-xl font-semibold leading-snug text-navy-950">{t(s.title)}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-graphite">{t(s.short)}</p>
                    </div>
                    <span className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-bold text-electric">
                      {lang === "fr" ? "En savoir plus" : "Learn more"}
                      <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DÉFIS & SOLUTIONS */}
      <section className="bg-paper py-20 md:py-28" data-testid="home-challenges-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(HOME.challenges.eyebrow)}</p>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-5xl">{t(HOME.challenges.title)}</h2>
          </Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {HOME.challenges.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.05} className="h-full">
                <div className="flex h-full flex-col gap-4 rounded-2xl border border-slate-200/90 bg-white p-7 transition-all duration-300 hover:border-sky-300 hover:shadow-card" data-testid={`challenge-card-${i}`}>
                  <p className="text-[15px] font-semibold leading-relaxed text-navy-900">{t(item.challenge)}</p>
                  <div className="mt-auto border-l-2 border-electric pl-4 pt-1">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-widest2 text-sky-700">{lang === "fr" ? "Notre solution" : "Our solution"}</p>
                    <p className="mt-2 text-sm leading-relaxed text-graphite">{t(item.solution)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AVANTAGES */}
      <section className="bg-white py-20 md:py-28" data-testid="home-advantages-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(HOME.advantages.eyebrow)}</p>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-5xl">{t(HOME.advantages.title)}</h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {HOME.advantages.items.map((item, i) => {
              const Icon = ICONS[item.icon];
              return (
                <Reveal key={i} delay={i * 0.06} className="h-full">
                  <div className="h-full rounded-2xl border border-slate-200/90 p-7 transition-all duration-300 hover:border-sky-300 hover:shadow-card" data-testid={`advantage-card-${i}`}>
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-badge text-sky-700">
                      <Icon size={20} strokeWidth={1.8} />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-semibold text-navy-950">{t(item.title)}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-graphite">{t(item.desc)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTEURS */}
      <section className="bg-paper py-20 md:py-28" data-testid="home-sectors-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(HOME.sectors.eyebrow)}</p>
            <div className="mt-4 flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <h2 className="max-w-xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-5xl">{t(HOME.sectors.title)}</h2>
              <p className="max-w-md text-[15px] leading-relaxed text-graphite">{t(HOME.sectors.intro)}</p>
            </div>
          </Reveal>
          <div className="mt-12 border-t border-slate-200">
            {SECTORS.items.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.04}>
                <Link
                  to={`/secteurs#${s.slug}`}
                  data-testid={`home-sector-row-${s.slug}`}
                  className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 border-b border-slate-200 py-6 transition-colors duration-300 hover:bg-ice md:gap-8 md:px-4"
                >
                  <span className="font-mono text-sm font-semibold text-steel">0{i + 1}</span>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-navy-950 transition-colors duration-300 group-hover:text-electric md:text-2xl">{t(s.title)}</h3>
                    <p className="mt-1 hidden max-w-2xl text-sm text-graphite md:block">{t(s.desc)}</p>
                  </div>
                  <ArrowUpRight size={20} className="text-steel transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-electric" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOURNISSEURS */}
      <section className="bg-white pb-20 md:pb-28" data-testid="home-suppliers-section">
        <div className="mx-auto grid max-w-[1320px] items-center gap-10 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(HOME.supplierBand.eyebrow)}</p>
              <h2 className="mt-4 font-display text-3xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-4xl">{t(HOME.supplierBand.title)}</h2>
              <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-graphite">{t(HOME.supplierBand.sub)}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Reveal delay={0.08}>
              <Link to="/fournisseurs" data-testid="home-supplier-cta" className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 text-sm font-bold text-navy-800 transition-colors hover:border-electric hover:text-electric">
                <Truck size={16} /> {t(HOME.supplierBand.cta)}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* À PROPOS TEASER */}
      <section className="bg-paper py-20 md:py-28" data-testid="home-about-section">
        <div className="mx-auto grid max-w-[1320px] items-center gap-12 px-5 md:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <img src={ABOUT_IMG} alt={lang === "fr" ? "Façade de verre contemporaine à haute performance technique" : "High-tech faceted glass architectural facade"} className="aspect-[4/3] w-full rounded-2xl object-cover shadow-card" loading="lazy" />
              <div className="absolute -bottom-6 -right-4 hidden h-28 w-28 items-center justify-center rounded-2xl bg-electric p-4 text-center shadow-lift md:flex">
                <p className="font-display text-sm font-semibold leading-snug text-white">{lang === "fr" ? "Bâti sur la rigueur." : "Built on rigour."}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(HOME.aboutTeaser.eyebrow)}</p>
            <h2 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-5xl">{t(HOME.aboutTeaser.title)}</h2>
            <p className="mt-6 text-base leading-relaxed text-graphite">{t(HOME.aboutTeaser.body)}</p>
            <Link to="/a-propos" data-testid="home-about-link" className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-electric">
              {t(HOME.aboutTeaser.link)}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 md:py-28" data-testid="home-faq-section">
        <div className="mx-auto max-w-[1320px] px-5 md:px-8">
          <Reveal>
            <p className="text-center font-mono text-xs font-semibold uppercase tracking-widest2 text-sky-700">{t(HOME.faq.eyebrow)}</p>
            <h2 className="mt-4 text-center font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy-950 md:text-5xl">{t(HOME.faq.title)}</h2>
          </Reveal>
          <div className="mt-12">
            <Faq items={HOME.faq.items} />
          </div>
        </div>
      </section>

      <CtaBand title={t(HOME.contact.title)} sub={t(HOME.contact.sub)} />
    </main>
  );
}
